import { FooterButton } from "pro-features/components/MultiCategoriesChipDown/src/MultiCategoryChipdownStyles";
import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import { i18nFilter } from "app/filters/i18nFilter";
import { ChipDownContainer, EllipsisDropdownItem } from "@similarweb/ui-components";
import type { IWebsiteKeywordsPageTableTopContext } from "app/pages/website-analysis/traffic-sources/search/components/WebsiteKeywordsPageContext";
import { useWebsiteKeywordsPageTableTopContext } from "app/pages/website-analysis/traffic-sources/search/components/WebsiteKeywordsPageContext";
import allSearchTypes from "app/pages/website-analysis/constants/searchTypes";
import { abbrNumberVisitsFilter } from "app/filters/ngFilters";
import { getDropdownTitle } from "pro-features/components/MultiChipDown/src/MultiChipDown";
import { FILTER_DEFAULT_SEPARATOR } from "app/pages/website-analysis/constants/constants";
import type { IWebsiteKeywordsPageFilters } from "../WebsiteKeywordsPageTypes";
import { allTrackers } from "app/services/track/track";

interface ISearchTypesFilterProps {
    tableFilters: IWebsiteKeywordsPageFilters;
    onSearchTypeChange: (selected: string[]) => void;
    withCount: boolean;
    searchTypes: IWebsiteKeywordsPageTableTopContext["searchTypes"];
}

const SearchTypesFilter: FC<ISearchTypesFilterProps> = ({
    tableFilters,
    onSearchTypeChange,
    withCount,
    searchTypes = [],
}) => {
    const initialSelected = useRef(tableFilters.source?.split(FILTER_DEFAULT_SEPARATOR) ?? []);
    const dropdownRef = useRef<{ closePopup?: VoidFunction }>(null);
    const [selected, setSelected] = useState(initialSelected.current);

    useEffect(() => {
        if (tableFilters.source) {
            initialSelected.current = tableFilters.source.split(FILTER_DEFAULT_SEPARATOR);
            setSelected(initialSelected.current);
        } else {
            setSelected([]);
            initialSelected.current = [];
        }
    }, [tableFilters.source]);

    const onToggle = (isOpen: boolean) => {
        if (isOpen) {
            allTrackers.dispatchTrackEvent("Drop Down", "open", "open Search Type filter");
        } else {
            onSearchTypeChange(selected ?? []);
        }
    };
    const onClick = ({ id }) => {
        const index = selected.indexOf(id);
        const newSelected = [...selected];
        if (index > -1) {
            newSelected.splice(index, 1);
            allTrackers.dispatchTrackEvent("Drop Down", "click", `deselect Search Type/${id || ""}`);
        } else {
            newSelected.push(id);
            allTrackers.dispatchTrackEvent("Drop Down", "click", `select Search Type/${id || ""}`);
        }
        setSelected(newSelected);
    };

    const allItems = allSearchTypes
        .map((type) => {
            const searchTypeCount = searchTypes.find((searchType) => searchType.id.toString() === type.id)?.count;
            return {
                ...type,
                count: searchTypeCount ?? 0,
            };
        })
        .sort((a, b) => b.count - a.count);

    const onClose = () => {
        dropdownRef?.current?.closePopup();
        onSearchTypeChange([]);
    };
    const onApply = () => {
        dropdownRef?.current?.closePopup();
        onSearchTypeChange(selected);

        allTrackers.dispatchTrackEvent("Button", "submit-ok", `Apply Search Type Filter/${selected.join(", ") || ""}`);
    };

    const allItemsElements = allItems.map((type) => {
        return (
            <EllipsisDropdownItem
                key={type.id}
                id={type.id}
                showCheckBox={true}
                selected={selected.includes(type.id)}
                infoText={withCount ? abbrNumberVisitsFilter()(type?.count || 0) : undefined}
                disabled={withCount && type.count === 0}
            >
                {type.text}
            </EllipsisDropdownItem>
        );
    });

    const selectedIds = selected.reduce((result, id) => {
        result[id] = true;
        return result;
    }, {});

    const selectedItemsTitles = selected.map((id) => allSearchTypes.find((type) => type.id === id)?.text);

    return (
        <div>
            <ChipDownContainer
                ref={dropdownRef}
                onToggle={onToggle}
                hasSearch={false}
                selectedIds={selectedIds}
                selectedText={getDropdownTitle(selectedItemsTitles)}
                buttonText={i18nFilter()("search.type.filter.placeholder")}
                onClick={onClick}
                onCloseItem={onClose}
                closeOnItemClick={false}
                width={300}
                footerComponent={() => (
                    <FooterButton onClick={onApply}>{i18nFilter()("serp.filter.apply")}</FooterButton>
                )}
                noResultsMessage={i18nFilter()("dropdown.no.results")}
            >
                {allItemsElements}
            </ChipDownContainer>
        </div>
    );
};

export const SearchTypesFilterForWebsiteKeywords = ({ withCount = true }) => {
    const { tableFilters, searchTypes, onSearchTypeChange } = useWebsiteKeywordsPageTableTopContext();
    return (
        <SearchTypesFilter
            tableFilters={tableFilters}
            searchTypes={searchTypes}
            onSearchTypeChange={onSearchTypeChange}
            withCount={withCount}
        />
    );
};
